import React, { useState } from "react";
import { ChevronDown, Check, Search, X } from "lucide-react";
import { cn } from "../../utils/cn";
import Button from "./Button";
import Input from "./Input";

export interface SelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
    description?: string;
}

export interface SelectProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'onChange' | 'defaultValue'> {
    options?: SelectOption[];
    value?: string | number | (string | number)[];
    defaultValue?: string | number | (string | number)[];
    placeholder?: string;
    multiple?: boolean;
    disabled?: boolean;
    required?: boolean;
    label?: string;
    description?: string;
    error?: string;
    searchable?: boolean;
    clearable?: boolean;
    loading?: boolean;
    name?: string;
    onChange?: (value: any) => void;
    onOpenChange?: (open: boolean) => void;
}

const Select = React.forwardRef<HTMLButtonElement, SelectProps>(({
    className,
    options = [],
    value,
    defaultValue,
    placeholder = "Select an option",
    multiple = false,
    disabled = false,
    required = false,
    label,
    description,
    error,
    searchable = false,
    clearable = false,
    loading = false,
    id,
    name,
    onChange,
    onOpenChange,
    ...props
}, ref) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>("");

    // Generate unique ID if not provided
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

    // Filter options based on search
    const filteredOptions = searchable && searchTerm
        ? options.filter(option =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (option.value && option.value.toString().toLowerCase().includes(searchTerm.toLowerCase()))
        )
        : options;

    // Get selected option(s) for display
    const getSelectedDisplay = (): string => {
        if (!value) return placeholder;

        if (multiple) {
            const valueArray = Array.isArray(value) ? value : [value];
            const selectedOptions = options.filter(opt => valueArray.includes(opt.value));
            if (selectedOptions.length === 0) return placeholder;
            if (selectedOptions.length === 1) return selectedOptions[0].label;
            return `${selectedOptions.length} items selected`;
        }

        const selectedOption = options.find(opt => opt.value === value);
        return selectedOption ? selectedOption.label : placeholder;
    };

    const handleToggle = (): void => {
        if (!disabled) {
            const newIsOpen = !isOpen;
            setIsOpen(newIsOpen);
            onOpenChange?.(newIsOpen);
            if (!newIsOpen) {
                setSearchTerm("");
            }
        }
    };

    const handleOptionSelect = (option: SelectOption): void => {
        if (multiple) {
            const currentValue = Array.isArray(value) ? value : [];
            const updatedValue = currentValue.includes(option.value)
                ? currentValue.filter(v => v !== option.value)
                : [...currentValue, option.value];
            onChange?.(updatedValue);
        } else {
            onChange?.(option.value);
            setIsOpen(false);
            onOpenChange?.(false);
        }
    };

    const handleClear = (e: React.MouseEvent): void => {
        e.stopPropagation();
        onChange?.(multiple ? [] : '');
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(e.target.value);
    };

    const isSelected = (optionValue: string | number): boolean => {
        if (multiple) {
            const valueArray = Array.isArray(value) ? value : [];
            return valueArray.includes(optionValue);
        }
        return value === optionValue;
    };

    const hasValue = multiple
        ? Array.isArray(value) && value.length > 0
        : value !== undefined && value !== '';

    return (
        <div className={cn("relative", className)}>
            {label && (
                <div className="flex justify-between items-end mb-2.5">
                    <label
                        htmlFor={selectId}
                        className={cn(
                            "text-[10px] font-black  tracking-[0.2em] leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                            error ? "text-red-700" : "text-[#0C2C39]/40"
                        )}
                    >
                        {label}
                        {required && <span className="text-red-700 ml-1">*</span>}
                    </label>
                    {description && !error && (
                        <span className="text-[9px] font-black  tracking-widest text-[#0C2C39]/20">
                            {description}
                        </span>
                    )}
                </div>
            )}

            <div className="relative">
                <button
                    ref={ref}
                    id={selectId}
                    type="button"
                    className={cn(
                        "flex h-12 w-full items-center justify-between rounded-xl border-2 border-[#0C2C39] bg-white text-[#0C2C39] px-4 py-3 text-sm font-black transition-all focus:outline-none focus:shadow-[2px_2px_0px_0px_#0C2C39] disabled:cursor-not-allowed disabled:opacity-50",
                        error && "border-red-700 focus:shadow-[2px_2px_0px_0px_rgba(185,28,28,1)] text-red-700",
                        !hasValue && "text-[#0C2C39]/30"
                    )}
                    onClick={handleToggle}
                    disabled={disabled}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    {...props}
                >
                    <span className="truncate  tracking-widest text-[10px]">{getSelectedDisplay()}</span>

                    <div className="flex items-center gap-2">
                        {loading && (
                            <svg className="animate-spin h-4 w-4 text-[#0C2C39]" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        )}

                        {clearable && hasValue && !loading && (
                            <button
                                className="h-4 w-4 text-[#0C2C39]/30 hover:text-red-700 transition-colors"
                                onClick={handleClear}
                            >
                                <X className="h-3 w-3" />
                            </button>
                        )}

                        <ChevronDown className={cn("h-4 w-4 transition-transform text-[#0C2C39]/50", isOpen && "rotate-180 text-[#0C2C39]")} />
                    </div>
                </button>

                {/* Hidden native select for form submission */}
                <select
                    name={name}
                    value={Array.isArray(value) ? value.map(v => String(v)) : value ? String(value) : ''}
                    onChange={() => { }} // Controlled by our custom logic
                    className="sr-only"
                    tabIndex={-1}
                    multiple={multiple}
                    required={required}
                >
                    <option value="">Select...</option>
                    {options.map(option => (
                        <option key={option.value} value={String(option.value)}>
                            {option.label}
                        </option>
                    ))}
                </select>

                {/* Dropdown */}
                {isOpen && (
                    <div className="absolute z-50 w-full mt-3 bg-white border-2 border-[#0C2C39] rounded-xl shadow-[4px_4px_0px_0px_#0C2C39] overflow-hidden animate-in fade-in zoom-in duration-200">
                        {searchable && (
                            <div className="p-3 border-b-2 border-[#0C2C39]/5">
                                <div className="relative">
                                    <Search className="absolute left-3 top-3 h-4 w-4 text-[#0C2C39]/30" />
                                    <Input
                                        placeholder="Search protocols..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        className="pl-10 h-10 rounded-lg"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="py-2 max-h-64 overflow-auto scrollbar-hide">
                            {filteredOptions.length === 0 ? (
                                <div className="px-5 py-4 text-[10px] font-black  tracking-widest text-[#0C2C39]/30 text-center">
                                    {searchTerm ? 'No matches found' : 'Empty directory'}
                                </div>
                            ) : (
                                filteredOptions.map((option) => (
                                    <div
                                        key={option.value}
                                        className={cn(
                                            "relative flex cursor-pointer select-none items-center px-5 py-4 text-[10px] font-black  tracking-widest transition-colors",
                                            isSelected(option.value)
                                                ? "bg-[#0C2C39] text-white"
                                                : "text-[#0C2C39] hover:bg-[#0C2C39]/5",
                                            option.disabled && "pointer-events-none opacity-30"
                                        )}
                                        onClick={() => !option.disabled && handleOptionSelect(option)}
                                    >
                                        <span className="flex-1">{option.label}</span>
                                        {multiple && isSelected(option.value) && (
                                            <Check className="h-4 w-4" />
                                        )}
                                        {option.description && (
                                            <span className={cn(
                                                "text-[8px] opacity-40 ml-3",
                                                isSelected(option.value) ? "text-white" : "text-[#0C2C39]"
                                            )}>
                                                {option.description}
                                            </span>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <p className="text-[10px] font-black  tracking-widest text-red-700 mt-2">
                    {error}
                </p>
            )}
        </div>
    );
});

Select.displayName = "Select";

export default Select;